import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import {
  Checkbox,
  IconButton,
} from '@mui/material';
import filterFunnelIcon from '../../../../assets/images/filter_funnel_icon.svg';
import velocityIcon from '../../../../assets/images/velocity_icon.svg';

const VelocityCard = ({ averageVelocity }) => {
  const { t } = useTranslation();
  const velocityValue = Number(
    averageVelocity?.velocity ??
    averageVelocity?.value ??
    averageVelocity ??
    0
  );
  const trend = String(averageVelocity?.trend || '').toUpperCase();
  const trendPercentage = Number(averageVelocity?.trend_percentage ?? 0);
  const hasTrend = Boolean(trend) && Number.isFinite(trendPercentage) && trendPercentage > 0;

  return (
    <div className="velocity-card">
      <div className="header-row">
        <span className="title">{t('sprintTimeline.avgVelocity')}</span>
        <img src={velocityIcon} alt="Velocity" className="velocity-icon" />
      </div>
      <hr />
      <div className="velocity-stats">
        <div className="sp-count-container">
          <span className="sp-number">{Number.isFinite(velocityValue) ? velocityValue : 0}</span>
          <span className="sp-label">{t('sprintTimeline.spPerSprint', { sp: '' }).replace(/^\s+|\s+$/g, '')}</span>
        </div>
        {hasTrend ? (
          <div className={`comparison ${trend === 'UP' ? 'increase' : 'decrease'}`}>
            {trend === 'UP' ? (
              <TrendingUp className="trend-velocity-icon" fontSize="small" />
            ) : (
              <TrendingDown className="trend-velocity-icon" fontSize="small" />
            )}
            <span>{`${trend === 'UP' ? '+' : '-'}${trendPercentage}% vs last 3 sprints`}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const FiltersCard = ({ filters, setFilters }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const handleToggle = (option) => {
    setFilters((prev) => {
      const nextValue = !prev[option];
      const selectedCount = Object.values(prev).filter(Boolean).length;

      if (!nextValue && selectedCount <= 1) {
        return prev;
      }

      return { ...prev, [option]: nextValue };
    });
  };

  const options = ['Completed', 'Active', 'Draft'];

  return (
    <div className="filters-card">
      <div className="filters-header" onClick={() => setExpanded(!expanded)}>
        <h3>
          <img src={filterFunnelIcon} alt="Filter" className="filter-icon" />
          {t('sprintTimeline.filters')}
        </h3>
        <IconButton size="small" disableRipple className="filter-expand-btn">
          {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        </IconButton>
      </div>

      {expanded && (
        <div className="filters-content">
          {options.map((option) => (
            <div key={option} className="filter-option" onClick={() => handleToggle(option)}>
              <Checkbox
                checked={Boolean(filters?.[option])}
                readOnly
                disableRipple
                tabIndex={-1}
                className="filter-checkbox"
              />
              <span className="filter-text">{option}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectSummaryCard = ({ summaryData, projectName, filters, setFilters }) => {
  const { t } = useTranslation();

  const projectSummary = summaryData?.project_summary || summaryData || {};
  const totalCount = Number(projectSummary.total_sprints || 0);
  const draftCount = Number(projectSummary.draft || 0);
  const activeCount = Number(projectSummary.active || 0);
  const completedCount = Number(projectSummary.completed || 0);
  const summaryProjectName = projectSummary.project_name || projectName || '';
  const averageVelocity = summaryData?.average_velocity ?? projectSummary.average_velocity;

  return (
    <>
      <div className="summary-card">
        <h3>
          {t('sprintTimeline.projectSummary')}
          <span className="project-name">{summaryProjectName}</span>
        </h3>
        <hr className="summary-separator" />
        <div className="summary-list">
          <div className="summary-item">
            <span className="label">{t('sprintTimeline.totalSprint')}</span>
            <span className="value val-total">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="label">{t('sprintTimeline.draftSprint')}</span>
            <span className="value val-draft">{draftCount}</span>
          </div>
          <div className="summary-item">
            <span className="label">{t('sprintTimeline.activeSprint')}</span>
            <span className="value val-active">{activeCount}</span>
          </div>
          <div className="summary-item">
            <span className="label">{t('sprintTimeline.completedSprint')}</span>
            <span className="value val-completed">{completedCount}</span>
          </div>
        </div>
      </div>

      <VelocityCard averageVelocity={averageVelocity} />
      <FiltersCard filters={filters} setFilters={setFilters} />
    </>
  );
};

export default ProjectSummaryCard;
